import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';

import type { SanityComparisonTable } from '#/lib/sanity/queries/comparisonTableSection';
import { cn } from '#/lib/utils';

import { BasicText } from '../BasicText';
import { CellIcon } from './CellIcon';

const AccordionIcons = ({
  headers,
  cells,
}: {
  headers: SanityComparisonTable['headers'];
  cells: SanityComparisonTable['rows'][number]['cells'];
}) => {
  return (
    <div className="grid grid-cols-3 gap-10 mt-8">
      {cells.map((cell, index) => (
        <div key={cell._key} className="flex items-start gap-10 flex-col ">
          <span className="font-[440] text-13 text-charcoal flex  items-center justify-center flex-1">
            {headers[index]}
          </span>
          <CellIcon key={cell._key} icon={cell.icon} className="" />
        </div>
      ))}
    </div>
  );
};
const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn('border-b border-grayscale-03', className)}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        'flex flex-1 items-start justify-between py-16 text-left text-18 font-[440] leading-124 transition-all [&[data-state=open]_.icon]:rotate-180',
        className,
      )}
      {...props}
    >
      {children}
      <div className="grid shrink-0 place-items-center relative">
        <svg
          className="w-25 h-25 icon transition-transform duration-300"
          viewBox="0 0 25 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.78516 10.5L12.7852 14.5L16.7852 10.5"
            stroke="#8E8E98"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8.78516 10.5L12.7852 14.5L16.7852 10.5"
            stroke="black"
            strokeOpacity="0.2"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down text-grayscale-04"
    {...props}
  >
    <div
      className={cn(
        'text-12 font-light leading-140 pb-16 pt-0 w-[95%] text-pretty ',
        className,
      )}
    >
      {children}
    </div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export function MobileAccordion(props: SanityComparisonTable) {
  return (
    <div className="md:hidden">
      <Accordion
        type="multiple"
        className=""
        defaultValue={[props.rows[0]._key]}
      >
        {props.rows.map((row) => (
          <AccordionItem value={row._key} key={row._key}>
            <AccordionTrigger>{row.featureLabel}</AccordionTrigger>
            <AccordionContent>
              <BasicText value={row.featureContent} />
              <AccordionIcons headers={props.headers} cells={row.cells} />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
